import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import Sheet from 'react-modal-sheet';
import PageTitle from './PageTitle';
import Button from './Button.tsx';
import UserSelectedCategoriesView from './UserSelectedCategories';
import UserSelectedPartnersView from './UserSelectedPartners';
import { SEARCH_PARAMS_FILTERS } from '../constants/searchParamsFilters.ts';
import { PartnerDetail, PartnerFavoriteSave, RequestData, YSH_GTM_EVENTS } from '../types/types.ts';
import { callPublicAPI } from '../utils/api.ts';
import close from '../assets/images/icons/icon-close.svg';
import FilterPartnerWithFavorites from './FilterPartnerWithFavorites.tsx';
import { useFetchSelectedCategories } from '../hooks/useFetchSelectedCategories.ts';
import { useFetchSelectedPartners } from '../hooks/useFetchSelectedPartners.ts';
import { pushToAnalytics } from '../utils/analytics.ts';
import '../scss/profile-interests.scss';

const ProfileInterests = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { data: selectedCategories } = useFetchSelectedCategories();
  const { data: selectedPartners } = useFetchSelectedPartners();
  const [userSelectedPartners, setUserSelectedPartners] = useState<PartnerDetail[]>([]);
  const [userSelectedCategories, setUserSelectedCategories] = useState<string[]>([]);

  useEffect(() => {
    setUserSelectedCategories(selectedCategories ?? []);
    setUserSelectedPartners(selectedPartners ?? []);
  }, [selectedCategories, selectedPartners]);

  const saveSelectedCategories = useMutation({
    mutationFn: async () => {
      const data: RequestData = {
        categories: userSelectedCategories
      };
      return await callPublicAPI<string[]>('POST', '/user-selected-categories', null, data);
    },
    onError: () => {
      alert('Žao nam je, nismo uspeli da sačuvamo kategorije');
    },
    onSettled: () => {
      setIsPopupOpen(false);
    }
  });

  const saveFavoritePartners = useMutation<PartnerFavoriteSave>({
    mutationFn: async () => {
      const data: RequestData = {
        partners: userSelectedPartners.map(partner => partner.id)
      };
      const response = await callPublicAPI<PartnerFavoriteSave>('POST', `/user-selected-partners`, null, data);
      return response.data;
    },
    onError: () => {
      alert('Žao nam je, nismo uspeli da sačuvamo partnere');
    },
    onSettled: () => {
      setIsPopupOpen(false);
    }
  });

  const handleAddMoreBrands = () => {
    pushToAnalytics({
      eventName: YSH_GTM_EVENTS.ADD_MORE_BRANDS,
      placement: 'my_interests',
      data: {}
    });

    setIsPopupOpen(true);
  };

  const handleSave = () => {
    saveFavoritePartners.mutate();
    saveSelectedCategories.mutate();
  };

  const handleSavePartners = () => {
    saveFavoritePartners.mutate();
  };

  useEffect(() => {
    pushToAnalytics({
      eventName: YSH_GTM_EVENTS.MY_INTERESTS,
      data: {}
    });
  }, []);

  return (
    <section className="content-width profile-interest-content">
      <PageTitle className="mb-4">Moja interesovanja</PageTitle>
      <UserSelectedCategoriesView
        userSelectedCategories={userSelectedCategories}
        setUserSelectedCategories={setUserSelectedCategories}
      />
      <UserSelectedPartnersView
        userSelectedPartners={userSelectedPartners}
        setUserSelectedPartners={setUserSelectedPartners}
      />

      <div className="position-fixed">
        <p onClick={handleAddMoreBrands} className="text-center mb-3 text-decoration-underline">
          +Dodaj još brendova
        </p>
        <Button onClick={handleSave}>Sačuvaj</Button>
      </div>
      <Sheet isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
        <Sheet.Container className="ps-3 pe-3">
          <Sheet.Header>
            <div className="d-flex justify-content-end pt-3">
              <img
                className="cursor-pointer"
                width={25}
                height={25}
                src={close}
                alt="close modal"
                onClick={() => setIsPopupOpen(false)}
              />
            </div>
          </Sheet.Header>
          <Sheet.Content>
            <FilterPartnerWithFavorites
              key={SEARCH_PARAMS_FILTERS.PARTNER_PARAM}
              userSelectedPartners={userSelectedPartners}
              handleSaveFilteredListOfFavouriteBrands={handleSavePartners}
              setUserSelectedPartners={setUserSelectedPartners}
              placement="my_interests"
            />
          </Sheet.Content>
        </Sheet.Container>
      </Sheet>
    </section>
  );
};

export default ProfileInterests;
